import axios, { isCancel, type AxiosRequestHeaders } from "axios";
import { showToastError } from "../components/ToastNotifications";
import {
  AUTH_STORAGE_KEY,
  DEFAULT_API_BASE_URL,
  REFRESH_STORAGE_KEY,
} from "../constants";
import { checkTokenExpiration, parseJwtPayload } from "./authTokens";
import { createRequestError, getErrorBody } from "./requestErrors";
// const AXIOS_NET_ERR = 'AxiosError Network Error';

const SKIP_REFRESH_ENDPOINTS = [
  "/session/login",
  "/session/refresh",
  "/session/create",
  "/session/resetpassword",
  "/confirm/email",
];

function shouldSkipRefresh(url?: string) {
  return SKIP_REFRESH_ENDPOINTS.some((endpoint) => (url || "").includes(endpoint));
}

let refreshPromise: Promise<void> | null = null;

async function refreshTokenIfNeeded(url?: string): Promise<void> {
  if (shouldSkipRefresh(url)) return;

  const currentToken = window.localStorage.getItem(AUTH_STORAGE_KEY);
  if (!currentToken) return;

  const tokenStatus = checkTokenExpiration(currentToken);
  if (!tokenStatus.isExpiringSoon && !tokenStatus.isExpired) return;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const payload = parseJwtPayload(currentToken);
        const organizationId =
          payload?.Permissions_0_OrganizationId ||
          payload?.permissions_0_organizationId ||
          1;
        const refreshToken = window.localStorage.getItem(REFRESH_STORAGE_KEY);

        if (!refreshToken) return;

        const refreshUrl = new URL(`${DEFAULT_API_BASE_URL}/session/refresh`);
        refreshUrl.searchParams.set("organizationId", String(organizationId));

        const refreshResp = await fetch(refreshUrl.toString(), {
          method: "POST",
          headers: {
            "x-refresh": refreshToken,
          },
        });

        if (!refreshResp.ok) {
          return;
        }

        const refreshData = await refreshResp.json().catch(() => null);
        if (refreshData?.token) {
          window.localStorage.setItem(AUTH_STORAGE_KEY, refreshData.token);
          window.dispatchEvent(new CustomEvent("tokenRefreshed", { detail: { token: refreshData.token } }));
        }
      } catch (error) {
        console.error("Error refreshing token:", error);
      } finally {
        refreshPromise = null;
      }
    })();
  }

  await refreshPromise;
}

function redirectToLogin(body: unknown) {
  if (window.location.href.includes("/login")) return;

  const message =
    typeof body === "string"
      ? body
      : typeof (body as { message?: unknown })?.message === "string"
        ? String((body as { message?: unknown }).message)
        : "";

  if (message) {
    showToastError(message);
    window.setTimeout(() => {
      window.location.assign("/login?to=1");
    }, 500);
    return;
  }

  window.location.assign("/login?to=1");
}

const axiosInstance = axios.create({
  baseURL: DEFAULT_API_BASE_URL,
  responseType: "json",
});

axiosInstance.interceptors.request.use(
  async function (config) {
    await refreshTokenIfNeeded(config.url);
    const jwtAuthToken = window.localStorage.getItem(AUTH_STORAGE_KEY) || "";
    if (config.ridPrintingCaller) {
      const claims = parseJwtPayload(jwtAuthToken);
      const actual = { id: String(claims?.Id ?? ''), issuer: claims?.iss, audience: JSON.stringify(claims?.aud) };
      if (JSON.stringify(actual) !== JSON.stringify(config.ridPrintingCaller)) {
        throw new Error('The signed-in account changed. Sign in with the original account to resume this print.');
      }
    }
    config.baseURL = DEFAULT_API_BASE_URL;
    config.headers = config.headers || ({} as AxiosRequestHeaders);
    if (jwtAuthToken) config.headers.Authorization = `Bearer ${jwtAuthToken}`;
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// intercepts the request and uses most recent token
// axiosInstance.interceptors.request.use(
//   function (config) {
//     // const jwtAuthToken = getLocal(AUTH_STORAGE_NAME);
//     if (jwtAuthToken) config.headers["x-access-token"] = jwtAuthToken;
//     return config;
//   },
//   function (error) {
//     return Promise.reject(error);
//   }
// );

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isCancel(error)) {
      return Promise.reject(error);
    }

    if (error?.response?.status === 401) {
      redirectToLogin(getErrorBody(error));
    }

    return Promise.reject(createRequestError(error));
  }
);

export default axiosInstance;

export { isCancel };
